/* eslint-disable replo/no-tailwind-literal-color */

import React, { useEffect, useMemo, useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";

import { CANVAS_DATA } from "@/features/canvas/canvas-constants";
import { useWebContainer } from "@/features/web-container/context";
import { useCachedPageContent } from "@/features/web-container/page-cache";
import twMerge from "@replo/design-system/utils/twMerge";
import useMeasure from "react-use-measure";
import { ReploError } from "schemas/errors";

import { useViewportStore } from "../store/viewportStore";
import PagePreview from "./PagePreview";

type PageRendererProps = {
  pageRoute: string;
  className?: string;
  /**
   * If true we will always render this page interactively from the WebContainer
   * (eventually). If false we will render a static version of the page from the
   * cache unless there is no cached version in which case we'll fall back to
   * the dynamically rendered one via WebContainer (which will be cached for
   * next time around).
   */
  isInteractive?: boolean;
  isScrollable?: boolean;
  previewThumbnail?: boolean;
  /**
   * Optional overlay elements to render on top of the page content.
   * These will be positioned correctly relative to the scaled iframe content.
   */
  overlayElements?: React.ReactNode;
};

class PageRouteRequiredError extends ReploError {
  constructor(pageRoute: string) {
    super({
      message: "Page route not found",
      additionalData: { pageRoute },
    });
  }
}

const MAX_RENDERS_BEFORE_ERROR = 10;
/**
 * This hook is used to get the page URL from the web container. It also handles
 * the case where the page is not yet found, requiring a rerender of the iframe
 * while waiting for the next dev server to catch up.
 *
 * NOTE (Gabe 2025-07-07): The page will not transition from isLoading until the
 * pageUrl is requested (for instance via an iframe).
 */
const usePageFromWebContainer = (pageRoute: string) => {
  const [initialRenderAt] = useState(Date.now());
  const [rerenderCount, setForceRerender] = useState(0);

  const { webContainerUrl, pageStatusMap } = useWebContainer();
  const pageUrl = useMemo(() => {
    return webContainerUrl && pageRoute
      ? `${webContainerUrl}/${pageRoute.replace(/^\//, "")}`
      : undefined;
  }, [pageRoute, webContainerUrl]);

  // NOTE (Gabe 2025-07-07): We use timestamp here to determine if this status
  // is a result of a request made for this render attempt (of PageRenderer) or
  // a previous render. If the timestamp indicates the status is from a previous
  // request, we treat this as loading while we wait for the next dev server to
  // serve the page. We also treat 404 as loading because it's usually an
  // indication that the page is not yet available (next dev server is still
  // catching up) (unless we've already rerendered 5 times).
  const isLoading =
    !pageStatusMap[pageRoute] ||
    pageStatusMap[pageRoute].timestamp < initialRenderAt ||
    (pageStatusMap[pageRoute]?.status === 404 &&
      rerenderCount < MAX_RENDERS_BEFORE_ERROR);

  const isError = !isLoading && pageStatusMap[pageRoute]?.status !== 200;

  useEffect(() => {
    if (
      pageStatusMap[pageRoute]?.status === 404 &&
      pageStatusMap[pageRoute]?.timestamp > initialRenderAt
    ) {
      const timeout = setTimeout(() => {
        setForceRerender((prev) =>
          prev < MAX_RENDERS_BEFORE_ERROR ? prev + 1 : prev,
        );
      }, 400);

      return () => clearTimeout(timeout);
    }
  }, [pageStatusMap, pageRoute, initialRenderAt]);

  return { pageUrl, isLoading, isError, iframeKey: rerenderCount };
};

function PageRenderer({
  pageRoute,
  className,
  isInteractive = false,
  isScrollable = true,
  previewThumbnail,
  overlayElements,
}: PageRendererProps) {
  if (!pageRoute) {
    throw new PageRouteRequiredError(pageRoute);
  }

  const projectId = useCurrentProjectId();

  const cachedPageHtml = useCachedPageContent({ pageRoute, projectId });

  const {
    pageUrl,
    isLoading: isLoadingPage,
    isError,
    iframeKey,
  } = usePageFromWebContainer(pageRoute);

  const currentViewport = useViewportStore((state) => state.currentViewport);

  const [containerRef, containerDimensions] = useMeasure();

  // Get viewport-specific width from CANVAS_DATA
  const viewportWidth = CANVAS_DATA[currentViewport].defaultFrameWidth;

  // Calculate scale based on context and measured container size
  const scale = React.useMemo(() => {
    if (previewThumbnail) {
      return 0.25;
    }

    if (containerDimensions.width > 0) {
      // Scale viewport content to fit the container width exactly
      return containerDimensions.width / viewportWidth;
    }

    return 1; // Default to no scaling
  }, [containerDimensions.width, viewportWidth, previewThumbnail]);

  return (
    <div
      ref={containerRef}
      className={twMerge("flex h-full w-full flex-col relative", className)}
    >
      <div
        className={twMerge("relative h-full w-full overflow-hidden", className)}
      >
        <div
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            width: `${100 / scale}%`,
            height: `${100 / scale}%`,
            overflow: "hidden",
          }}
          className="flex h-full flex-col"
        >
          <div className="flex h-full w-full flex-col">
            <div className="relative flex h-full w-full flex-col items-center justify-center">
              {isError && (
                <div className="absolute inset-0 flex items-center text-6xl justify-center text-red-500 bg-red-50">
                  Error loading page
                </div>
              )}
              <PagePreview
                cachedPageHtml={cachedPageHtml}
                isLoadingPage={isLoadingPage}
                isInteractive={isInteractive}
                isScrollable={isScrollable}
              />
              {/* NOTE (Gabe 2025-07-14): This is rendered even if isLoadingPage is true because this iframe is
              what requests the page from the web container, causing it to transition to loaded. Also if there
              is no cachedPageHtml, we render the iframe to request the page from the web container (which will
              be cached next time). */}
              {(isInteractive || !cachedPageHtml) && (
                <iframe
                  src={pageUrl}
                  key={iframeKey}
                  className={twMerge(
                    "h-full w-full",
                    isLoadingPage && "opacity-0 pointer-events-none",
                  )}
                  // NOTE (Ben 2025-07-17): This is needed to prevent the iframe from being included in window
                  // history and messing up navigation.
                  sandbox="allow-scripts allow-same-origin"
                />
              )}
            </div>
          </div>
          {/* Overlay Elements - positioned relative to scaled content */}
          {overlayElements && (
            <div className="absolute inset-0 pointer-events-none z-[99999]">
              {overlayElements}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageRenderer;
